import React, { useEffect, useState } from 'react';
import { useLang } from '../lang/LangContext';
import {
  KYC_DOC_KEYS, AI_ISSUE_TEXT, FLAG_TEXT, KIND_TEXT, documentReview, submissionState, stampMillis,
  metadataUrl, photoSourceFromMetadata,
} from '../utils/kycReview';

// Where a photo came from ("camera" | "gallery") lives in the Storage object's metadata, written by
// uploadDriverPhotoSecure. Read it once per URL; if it can't be read, show nothing.
const sourceCache = new Map();

function PhotoSource({ url, label }) {
  const [source, setSource] = useState(() => sourceCache.get(url) || '');
  useEffect(() => {
    if (!url || sourceCache.has(url)) return undefined;
    const meta = metadataUrl(url);
    if (!meta) return undefined;
    let cancelled = false;
    fetch(meta)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const found = photoSourceFromMetadata(json);
        sourceCache.set(url, found);
        if (!cancelled) setSource(found);
      })
      .catch(() => { sourceCache.set(url, ''); });
    return () => { cancelled = true; };
  }, [url]);
  if (!url || !source) return null;
  return (
    <span className={`badge ${source === 'gallery' ? 'orange' : 'gray'}`} title="Where this photo came from, as reported by the app">
      {label}: {source === 'camera' ? '📷 camera' : '🖼️ gallery'}
    </span>
  );
}

const AI_CHIP = {
  ok: ['green', 'AI read it OK'],
  check: ['yellow', 'AI: please check'],
  retake: ['red', 'AI: could not read it'],
  unchecked: ['gray', 'AI did not run'],
};

function DocRow({ driver, docKey, t }) {
  const r = documentReview(driver, docKey);
  const docs = driver.documents || {};
  const e = docs[docKey] || {};
  const photos = docKey === 'drivingLicence'
    ? [['front', e.frontPhotoUrl], ['back', e.backPhotoUrl]]
    : docKey === 'insurance'
      ? [['photo', e.photoUrl]]
      : [['front', e.photoUrl], ['back', e.backPhotoUrl]];
  const [aiColor, aiText] = r.ai ? (AI_CHIP[r.ai.status] || AI_CHIP.unchecked) : [];

  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: 10, border: `1px solid ${r.ready ? 'rgba(255,255,255,0.06)' : 'rgba(239,68,68,0.45)'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>
          {r.ready ? '✅' : '⚠️'} {t(r.label)}
        </strong>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {r.ai && <span className={`badge ${aiColor}`}>{t(aiText)}</span>}
          {r.flags.map((f) => (
            <span key={f} className={`badge ${f === 'expiring_soon' ? 'yellow' : 'red'}`}>{t(FLAG_TEXT[f] || f)}</span>
          ))}
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {t('Entered')}: <strong className="mono">{r.number || '—'}</strong>
        {docKey !== 'rc' && docKey !== 'aadhaar' && <> · {t('Expiry')}: <strong>{r.expiryDate || '—'}</strong>{r.expiry.days !== null ? ` (${r.expiry.days} ${t('days')})` : ''}</>}
        {r.expirySuggestion && <span style={{ color: '#38bdf8' }}> · {t('Possibly')} <strong>{r.expirySuggestion}</strong></span>}
        {!r.hasPhoto && <span style={{ color: '#f87171' }}> · {t('No photo')}</span>}
        {r.hasBackPhoto === false && r.hasPhoto && <span style={{ color: '#f87171' }}> · {t('No back photo')}</span>}
      </div>
      {r.ai && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {t('AI read')}: <strong className="mono">{r.ai.number || r.ai.vehicleNumber || '—'}</strong>
          {docKey !== 'rc' && <> · {t('Expiry')}: <strong>{r.ai.expiryDate || '—'}</strong></>}
          {r.ai.name ? <> · {t('Name')}: {r.ai.name}</> : null}
          {r.ai.expiryKind ? (
            <div style={{ marginTop: 2 }}>
              {t('Validity read')}: <strong>{t(KIND_TEXT[r.ai.expiryKind] || r.ai.expiryKind)}</strong>
              {r.ai.expiryLabel ? <span> ({t('printed')}: “{r.ai.expiryLabel}”)</span> : null}
              {r.ai.sides > 1 ? <span> · {t('both sides read')}</span> : null}
            </div>
          ) : null}
          {r.ai.issues.length > 0 && (
            <div style={{ color: '#fbbf24', marginTop: 2 }}>
              {r.ai.issues.map((i) => t(AI_ISSUE_TEXT[i] || i)).join(' · ')}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {photos.map(([name, url]) => <PhotoSource key={name} url={url} label={t(name)} />)}
      </div>
    </div>
  );
}

/**
 * The first thing a reviewer sees in the documents window: has this Anna submitted, which of
 * the four mandatory documents are in order, what the AI read next to what he typed, and where
 * each photo came from. Advice only: the Approve / Reject buttons below are still the decision.
 */
export default function KycReviewCard({ driver }) {
  const { t } = useLang();
  const state = submissionState(driver);
  const submittedAt = stampMillis(driver.kycSubmittedAt);
  const aiRan = driver.kycAi && Object.keys(driver.kycAi).length > 0;

  return (
    <section style={{ background: '#0f172a', borderRadius: 16, padding: 16, marginBottom: 16, border: '1px solid rgba(56,189,248,0.28)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>📋 {t('Sign-up check')}</h3>
          <p className="muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
            {aiRan
              ? t('The AI only reads the photos. You decide: it never verifies or rejects.')
              : t('The AI reading is off or has not run. Check the photos yourself.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {state === 'submitted' && (
            <span className="badge green">📋 {t('Submitted')} {submittedAt ? new Date(submittedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
          )}
          {state === 'not_submitted' && (
            <span className="badge gray" title={t('Still filling in documents, or signed up on an older app version')}>{t('Not submitted')}</span>
          )}
          {state === 'approved' && <span className="badge green">{t('Approved')}</span>}
          <PhotoSource url={driver.photoUrl} label={t('Selfie')} />
          <PhotoSource url={driver.vehiclePhotoUrl} label={t('Auto photo')} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 10 }}>
        {KYC_DOC_KEYS.map((key) => <DocRow key={key} driver={driver} docKey={key} t={t} />)}
      </div>
    </section>
  );
}
